/**
 * テナント管理ページ (Presentational)
 */
import { Building, Check, Copy, Link2Off, Save, Send } from "lucide-react";
import { type FC, useCallback, useMemo, useRef, useState } from "react";
import { ToggleUI } from "../../../components/Toggle";
import { LICENSE_LABELS, type LicenseKey } from "../../../constants/licenses";
import type {
  SlackChannel,
  SlackIntegration,
} from "../../../hooks/use-slack-integration-api";
import type { TenantDetail, TenantMember } from "../../../hooks/useTenantApi";
import { CRM_ROLE_LABELS, CRM_ROLE_OPTIONS } from "../../../utils/crm-utils";

type TenantPageProps = {
  tenant: TenantDetail;
  intakeAddress: string;
  onUpdate: (input: { name?: string; description?: string }) => Promise<void>;
  isUpdating: boolean;
  currentUserId: string;
  onRoleChange: (userId: string, newRole: string) => Promise<void>;
  isRoleChanging: boolean;
  onCrmRoleChange: (userId: string, crmRole: string | null) => Promise<void>;
  isCrmRoleChanging: boolean;
  autoMatchingEnabled: boolean;
  onAutoMatchingToggle: (enabled: boolean) => Promise<void>;
  isAutoMatchingUpdating: boolean;
  // Slack連携
  slackIntegration: SlackIntegration | null;
  slackLoading: boolean;
  slackChannels: SlackChannel[];
  onSlackConnect: () => void;
  onSlackDisconnect: () => Promise<void>;
  onSlackChannelChange: (
    channelId: string,
    channelName: string,
  ) => Promise<void>;
  onSlackTestSend: () => Promise<{ success: boolean; message: string }>;
  onSlackChannelsFetch: () => Promise<void>;
  isSlackUpdating: boolean;
};

/** ロールの日本語ラベル */
const ROLE_LABELS: Record<string, string> = {
  owner: "オーナー",
  admin: "管理者",
  member: "メンバー",
};

export const TenantPage: FC<TenantPageProps> = ({
  tenant,
  intakeAddress,
  onUpdate,
  isUpdating,
  currentUserId,
  onRoleChange,
  isRoleChanging,
  onCrmRoleChange,
  isCrmRoleChanging,
  autoMatchingEnabled,
  onAutoMatchingToggle,
  isAutoMatchingUpdating,
  slackIntegration,
  slackLoading,
  slackChannels,
  onSlackConnect,
  onSlackDisconnect,
  onSlackChannelChange,
  onSlackTestSend,
  onSlackChannelsFetch,
  isSlackUpdating,
}) => {
  const [name, setName] = useState(tenant.name);
  const [description, setDescription] = useState(tenant.description ?? "");
  const [copied, setCopied] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [testResult, setTestResult] = useState<string | null>(null);
  const [showDisconnectConfirm, setShowDisconnectConfirm] = useState(false);
  const [roleChangeConfirm, setRoleChangeConfirm] = useState<{
    userId: string;
    name: string;
    newRole: string;
    label: string;
  } | null>(null);
  const [crmRoleChangeConfirm, setCrmRoleChangeConfirm] = useState<{
    userId: string;
    name: string;
    newCrmRole: string | null;
    label: string;
  } | null>(null);

  const hasCrmLicense = tenant.licenses.includes("CRM");

  const isDirty =
    name !== tenant.name || description !== (tenant.description ?? "");

  const handleCopy = useCallback(async () => {
    await navigator.clipboard.writeText(intakeAddress);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }, [intakeAddress]);

  const handleSave = useCallback(async () => {
    const input: { name?: string; description?: string } = {};
    if (name !== tenant.name) input.name = name;
    if (description !== (tenant.description ?? ""))
      input.description = description;

    await onUpdate(input);
    setSaveSuccess(true);
    setTimeout(() => setSaveSuccess(false), 2000);
  }, [name, description, tenant, onUpdate]);

  return (
    <div className="space-y-6">
      {/* ヘッダー */}
      <div>
        <h1 className="text-2xl font-bold flex items-center gap-2">
          <Building className="w-6 h-6" />
          テナント管理
        </h1>
        <p className="text-base-content/60 mt-1">
          テナントの設定情報を管理できます
        </p>
      </div>

      {/* テナント基本情報 */}
      <div className="card bg-base-100 shadow-sm">
        <div className="card-body">
          <h2 className="card-title text-lg">基本情報</h2>
          <div className="space-y-4 mt-2">
            <div className="form-control">
              <label className="label">
                <span className="label-text font-medium">テナント名</span>
              </label>
              <input
                type="text"
                className="input input-bordered w-full max-w-md"
                value={name}
                onChange={e => setName(e.target.value)}
                maxLength={255}
              />
            </div>
            <div className="form-control">
              <label className="label">
                <span className="label-text font-medium">説明</span>
              </label>
              <textarea
                className="textarea textarea-bordered w-full max-w-md"
                value={description}
                onChange={e => setDescription(e.target.value)}
                rows={3}
                maxLength={1000}
              />
            </div>
            <button
              type="button"
              className="btn btn-primary btn-sm"
              disabled={!isDirty || isUpdating || !name.trim()}
              onClick={handleSave}
            >
              {isUpdating ? (
                <span className="loading loading-spinner loading-xs" />
              ) : saveSuccess ? (
                <Check className="w-4 h-4" />
              ) : (
                <Save className="w-4 h-4" />
              )}
              {saveSuccess ? "保存しました" : "保存"}
            </button>
          </div>
        </div>
      </div>

      {/* メール取り込みアドレス */}
      <div className="card bg-base-100 shadow-sm">
        <div className="card-body">
          <h2 className="card-title text-lg">メール取り込みアドレス</h2>
          <p className="text-sm text-base-content/60 mt-1">
            以下のアドレスにメールを転送すると、Strix
            Searchで検索・閲覧できるようになります。
          </p>
          <div className="flex items-center gap-2 mt-3">
            <code className="bg-base-200 px-4 py-2 rounded-lg text-sm font-mono select-all flex-1 max-w-lg break-all">
              {intakeAddress}
            </code>
            <button
              type="button"
              className="btn btn-outline btn-sm gap-1"
              onClick={handleCopy}
            >
              {copied ? (
                <>
                  <Check className="w-4 h-4 text-success" />
                  コピー済み
                </>
              ) : (
                <>
                  <Copy className="w-4 h-4" />
                  コピー
                </>
              )}
            </button>
          </div>
          <div className="mt-3 text-sm text-base-content/50">
            <p>
              ご利用のメールサービス（Gmail、Outlook等）の転送設定で、上記アドレスを転送先として登録してください。
            </p>
          </div>
        </div>
      </div>

      {/* ライセンス状況 */}
      <div className="card bg-base-100 shadow-sm">
        <div className="card-body">
          <h2 className="card-title text-lg">ライセンス</h2>
          <div className="flex flex-wrap gap-2 mt-2">
            {tenant.licenses.length > 0 ? (
              tenant.licenses.map(key => (
                <span key={key} className="badge badge-primary badge-lg gap-1">
                  <Check className="w-3 h-3" />
                  {LICENSE_LABELS[key as LicenseKey] ?? key}
                </span>
              ))
            ) : (
              <span className="text-sm text-base-content/50">
                有効なライセンスはありません
              </span>
            )}
          </div>
        </div>
      </div>

      {/* マッチング設定 */}
      {tenant.licenses.includes("MATCHING") && (
        <div className="card bg-base-100 shadow-sm">
          <div className="card-body">
            <h2 className="card-title text-lg">マッチング設定</h2>
            <div className="form-control mt-2">
              <div className="flex items-center justify-between">
                <div>
                  <span className="label-text font-medium">自動マッチング</span>
                  <p className="text-base-content/60 text-sm mt-1">
                    メール受信時にAIマッチングを自動実行します
                  </p>
                </div>
                <ToggleUI
                  checked={autoMatchingEnabled}
                  disabled={isAutoMatchingUpdating}
                  onChange={e => onAutoMatchingToggle(e.target.checked)}
                />
              </div>
            </div>
            {autoMatchingEnabled && (
              <div className="alert alert-warning mt-3">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="stroke-current shrink-0 h-5 w-5"
                  fill="none"
                  viewBox="0 0 24 24"
                  role="img"
                  aria-label="警告"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                  />
                </svg>
                <span className="text-sm">
                  自動マッチングが有効な間は、受信したすべてのメールに対してAI処理が実行されるため、従量課金額が高くなる場合があります。
                </span>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Slack連携 */}
      {tenant.licenses.includes("MATCHING") && (
        <div className="card bg-base-100 shadow-sm">
          <div className="card-body">
            <h2 className="card-title text-lg">Slack連携</h2>
            {slackLoading ? (
              <div className="flex justify-center py-4">
                <span className="loading loading-spinner loading-md" />
              </div>
            ) : slackIntegration?.isConnected ? (
              <div className="space-y-4 mt-2">
                {/* 連携済み情報 */}
                <div className="flex items-center gap-2">
                  <span className="badge badge-success badge-sm gap-1">
                    <Check className="w-3 h-3" />
                    連携済み
                  </span>
                  {slackIntegration.teamName && (
                    <span className="text-sm text-base-content/70">
                      {slackIntegration.teamName}
                    </span>
                  )}
                </div>

                {/* チャンネル選択 */}
                <SlackChannelPicker
                  channels={slackChannels}
                  selectedChannelId={slackIntegration.channelId}
                  selectedChannelName={slackIntegration.channelName}
                  onSelect={(id, name) => onSlackChannelChange(id, name)}
                  onOpen={() => {
                    if (slackChannels.length === 0) onSlackChannelsFetch();
                  }}
                  disabled={isSlackUpdating}
                />

                {/* テスト送信 + 連携解除 */}
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    className="btn btn-outline btn-sm gap-1"
                    onClick={async () => {
                      setTestResult(null);
                      const result = await onSlackTestSend();
                      setTestResult(result.message);
                      setTimeout(() => setTestResult(null), 3000);
                    }}
                    disabled={!slackIntegration.channelId || isSlackUpdating}
                  >
                    <Send className="w-4 h-4" />
                    テスト送信
                  </button>
                  <button
                    type="button"
                    className="btn btn-outline btn-error btn-sm gap-1"
                    onClick={() => setShowDisconnectConfirm(true)}
                    disabled={isSlackUpdating}
                  >
                    <Link2Off className="w-4 h-4" />
                    連携解除
                  </button>
                </div>

                {testResult && (
                  <div className="alert alert-info py-2">
                    <span className="text-sm">{testResult}</span>
                  </div>
                )}

                {/* 連携解除確認ダイアログ */}
                {showDisconnectConfirm && (
                  <div className="alert alert-warning">
                    <div className="flex flex-col gap-2">
                      <span className="text-sm">
                        Slack連携を解除しますか？マッチング結果のSlack通知が届かなくなります。
                      </span>
                      <div className="flex gap-2">
                        <button
                          type="button"
                          className="btn btn-error btn-xs"
                          onClick={async () => {
                            await onSlackDisconnect();
                            setShowDisconnectConfirm(false);
                          }}
                          disabled={isSlackUpdating}
                        >
                          {isSlackUpdating ? (
                            <span className="loading loading-spinner loading-xs" />
                          ) : null}
                          解除する
                        </button>
                        <button
                          type="button"
                          className="btn btn-ghost btn-xs"
                          onClick={() => setShowDisconnectConfirm(false)}
                        >
                          キャンセル
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div className="mt-2">
                <p className="text-sm text-base-content/60">
                  Slackと連携して、マッチング結果の通知を受け取れます。
                </p>
                <button
                  type="button"
                  className="btn btn-sm mt-3 gap-2 text-white border-none hover:opacity-90"
                  style={{ backgroundColor: "#4A154B" }}
                  onClick={onSlackConnect}
                >
                  <img
                    src="/images/slack-logo.png"
                    alt="Slack"
                    className="w-5 h-5"
                  />
                  Slackと連携する
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* メンバー一覧 */}
      <div className="card bg-base-100 shadow-sm">
        <div className="card-body">
          <h2 className="card-title text-lg">
            メンバー一覧
            <span className="badge badge-ghost badge-sm">
              {tenant.members.length}名
            </span>
          </h2>
          <div className="overflow-x-auto mt-2">
            <table className="table table-sm">
              <thead>
                <tr>
                  <th>名前</th>
                  <th>メールアドレス</th>
                  <th>ロール</th>
                  {hasCrmLicense && <th>CRMロール</th>}
                </tr>
              </thead>
              <tbody>
                {tenant.members.map((member: TenantMember) => {
                  const isOwner = member.role === "owner";
                  const isSelf = member.userId === currentUserId;
                  const canChange = !isOwner && !isSelf;

                  return (
                    <tr key={member.userId}>
                      <td>
                        {member.familyName} {member.givenName}
                      </td>
                      <td className="text-base-content/70">{member.email}</td>
                      <td>
                        {canChange ? (
                          <select
                            className="select select-bordered select-xs"
                            value={member.role}
                            disabled={isRoleChanging}
                            onChange={e => {
                              const newRole = e.target.value;
                              if (newRole === member.role) return;
                              setRoleChangeConfirm({
                                userId: member.userId,
                                name: `${member.familyName} ${member.givenName}`,
                                newRole,
                                label: ROLE_LABELS[newRole] ?? newRole,
                              });
                            }}
                          >
                            <option value="admin">管理者</option>
                            <option value="member">メンバー</option>
                          </select>
                        ) : (
                          <span
                            className={`badge badge-sm ${
                              isOwner
                                ? "badge-primary"
                                : member.role === "admin"
                                  ? "badge-secondary"
                                  : "badge-ghost"
                            }`}
                          >
                            {ROLE_LABELS[member.role] ?? member.role}
                          </span>
                        )}
                      </td>
                      {hasCrmLicense && (
                        <td>
                          {isOwner ? (
                            <span className="badge badge-primary badge-sm">
                              管理者
                            </span>
                          ) : (
                            <select
                              className="select select-bordered select-xs"
                              value={member.crmRole ?? ""}
                              disabled={isCrmRoleChanging || isSelf}
                              onChange={e => {
                                const value = e.target.value;
                                const newCrmRole = value === "" ? null : value;
                                if (newCrmRole === member.crmRole) return;
                                setCrmRoleChangeConfirm({
                                  userId: member.userId,
                                  name: `${member.familyName} ${member.givenName}`,
                                  newCrmRole,
                                  label: newCrmRole
                                    ? (CRM_ROLE_LABELS[
                                        newCrmRole as keyof typeof CRM_ROLE_LABELS
                                      ] ?? newCrmRole)
                                    : "なし",
                                });
                              }}
                            >
                              <option value="">なし</option>
                              {CRM_ROLE_OPTIONS.map(opt => (
                                <option key={opt.value} value={opt.value}>
                                  {opt.label}
                                </option>
                              ))}
                            </select>
                          )}
                        </td>
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* ロール変更確認モーダル */}
      {roleChangeConfirm && (
        <div className="modal modal-open">
          <div className="modal-box">
            <h3 className="font-bold text-lg">ロールを変更しますか？</h3>
            <p className="py-4 text-base-content/70">
              {roleChangeConfirm.name} のロールを「{roleChangeConfirm.label}
              」に変更します。
            </p>
            <div className="modal-action">
              <button
                type="button"
                onClick={() => setRoleChangeConfirm(null)}
                disabled={isRoleChanging}
                className="btn btn-ghost"
              >
                キャンセル
              </button>
              <button
                type="button"
                onClick={async () => {
                  const { userId, newRole } = roleChangeConfirm;
                  try {
                    await onRoleChange(userId, newRole);
                  } catch (_e) {
                    // エラーは上位で処理
                  } finally {
                    setRoleChangeConfirm(null);
                  }
                }}
                disabled={isRoleChanging}
                className="btn btn-primary"
              >
                {isRoleChanging ? (
                  <span className="loading loading-spinner loading-sm" />
                ) : (
                  "変更する"
                )}
              </button>
            </div>
          </div>
          <div
            className="modal-backdrop"
            onClick={() => !isRoleChanging && setRoleChangeConfirm(null)}
          />
        </div>
      )}

      {/* CRMロール変更確認モーダル */}
      {crmRoleChangeConfirm && (
        <div className="modal modal-open">
          <div className="modal-box">
            <h3 className="font-bold text-lg">CRMロールを変更しますか？</h3>
            <p className="py-4 text-base-content/70">
              {crmRoleChangeConfirm.name} のCRMロールを「
              {crmRoleChangeConfirm.label}」に変更します。
            </p>
            <div className="modal-action">
              <button
                type="button"
                onClick={() => setCrmRoleChangeConfirm(null)}
                disabled={isCrmRoleChanging}
                className="btn btn-ghost"
              >
                キャンセル
              </button>
              <button
                type="button"
                onClick={async () => {
                  const { userId, newCrmRole } = crmRoleChangeConfirm;
                  try {
                    await onCrmRoleChange(userId, newCrmRole);
                  } catch (_e) {
                    // エラーは上位で処理
                  } finally {
                    setCrmRoleChangeConfirm(null);
                  }
                }}
                disabled={isCrmRoleChanging}
                className="btn btn-primary"
              >
                {isCrmRoleChanging ? (
                  <span className="loading loading-spinner loading-sm" />
                ) : (
                  "変更する"
                )}
              </button>
            </div>
          </div>
          <div
            className="modal-backdrop"
            onClick={() => !isCrmRoleChanging && setCrmRoleChangeConfirm(null)}
          />
        </div>
      )}
    </div>
  );
};

/**
 * テキスト検索付きSlackチャンネル選択コンポーネント
 * @param {object} props コンポーネントprops
 * @return {JSX.Element} チャンネル選択UI
 */
const SlackChannelPicker: FC<{
  channels: SlackChannel[];
  selectedChannelId: string | null;
  selectedChannelName: string | null;
  onSelect: (channelId: string, channelName: string) => void;
  onOpen: () => void;
  disabled: boolean;
}> = ({
  channels,
  selectedChannelId,
  selectedChannelName,
  onSelect,
  onOpen,
  disabled,
}) => {
  const [query, setQuery] = useState("");
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const filtered = useMemo(() => {
    if (!query) return channels;
    const q = query.toLowerCase();
    return channels.filter(ch => ch.name.toLowerCase().includes(q));
  }, [channels, query]);

  const handleFocus = useCallback(() => {
    setIsOpen(true);
    onOpen();
  }, [onOpen]);

  const handleSelect = useCallback(
    (ch: SlackChannel) => {
      onSelect(ch.id, ch.name);
      setQuery("");
      setIsOpen(false);
    },
    [onSelect],
  );

  // 外側クリックで閉じる
  const handleBlur = useCallback((e: React.FocusEvent) => {
    if (
      containerRef.current &&
      !containerRef.current.contains(e.relatedTarget as Node)
    ) {
      setIsOpen(false);
      setQuery("");
    }
  }, []);

  const displayValue = selectedChannelName ?? "";

  return (
    <div className="form-control">
      <label className="label">
        <span className="label-text font-medium">通知先チャンネル</span>
      </label>
      <div ref={containerRef} className="relative max-w-sm" onBlur={handleBlur}>
        <input
          type="text"
          className="input input-bordered w-full"
          placeholder={displayValue || "チャンネルを検索..."}
          value={isOpen ? query : displayValue}
          onChange={e => setQuery(e.target.value)}
          onFocus={handleFocus}
          disabled={disabled}
        />
        {isOpen && (
          <ul className="absolute z-50 mt-1 w-full max-h-60 overflow-y-auto bg-base-100 border border-base-300 rounded-box shadow-lg">
            {filtered.length === 0 ? (
              <li className="px-4 py-2 text-base-content/50 text-sm">
                {channels.length === 0
                  ? "読み込み中..."
                  : "一致するチャンネルがありません"}
              </li>
            ) : (
              filtered.map(ch => (
                <li key={ch.id}>
                  <button
                    type="button"
                    className={`w-full text-left px-4 py-2 text-sm hover:bg-base-200 cursor-pointer flex items-center gap-1 ${
                      ch.id === selectedChannelId
                        ? "bg-primary/10 font-medium"
                        : ""
                    }`}
                    onMouseDown={e => e.preventDefault()}
                    onClick={() => handleSelect(ch)}
                  >
                    <span className="text-base-content/50">
                      {ch.isPrivate ? "🔒" : "#"}
                    </span>
                    <span>{ch.name}</span>
                  </button>
                </li>
              ))
            )}
          </ul>
        )}
      </div>
    </div>
  );
};
